import {
  Box,
  Table,
  Tr,
  Th,
  Text,
  Thead,
  Tbody,
  Td,
  Flex,
  useToast,
  Icon,
  Tooltip,
  Stack,
  Button,
} from '@chakra-ui/react';
import { setIn } from 'formik';
import * as _ from 'lodash';
import React, { useState, useEffect, useCallback } from 'react';

import {
  checkIntermediateCalcErrors,
  IntermediateCalcError,
  isRequiredIntermediateFieldName,
  prettyPrintAllFormulas,
  RequiredIntermediateFieldNames,
} from '@finley-technologies/calc-agent';
import { FormulaEngine } from '@finley-technologies/finley-types';

import { CreditAgreementFullArtifactWipConfigType } from '../../types';
import { EditIntermediateCalcRow } from '../edit/EditIntermediateCalcRow';
import { colors } from 'packages/common-frontend/colors';
import { useDraggableRow } from 'packages/common-frontend/components/DragAndDropTable/hooks';
import { TrDraggableHandle } from 'packages/common-frontend/components/DragAndDropTable/ui';
import { FormulaContainer } from 'packages/common-frontend/components/FormulaContainer';
import {
  DeleteIconButton,
  EditIconButton,
} from 'packages/common-frontend/components/IconButton';
import { Icon as FinleyIcon } from 'packages/common-frontend/components/icons';
import {
  errorToastAdminConfig,
  errorToastId,
} from 'packages/common-frontend/constants';
import {
  CreditAgreement,
  getIterationKey,
} from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/common';
import { FiAlertTriangle, FiCheck } from 'react-icons/fi';
import { useFeatureFlags } from '~/contexts/FeatureFlagProvider';

const getFormulaIcon = (errors: IntermediateCalcError | null) => {
  if (errors === null) {
    return <Icon as={FiCheck} color="green.300" />;
  }
  return (
    <Tooltip
      label={
        <Stack>
          {errors.errorMessages.map((e: string) => (
            <Text key={e}>{e}</Text>
          ))}
        </Stack>
      }
      placement="auto-start"
    >
      <Box>
        <Icon as={FiAlertTriangle} color="red.300" />
      </Box>
    </Tooltip>
  );
};

const createIntermediateCalcShell = (
  index: number,
): CreditAgreement.Config.LoanTapeField.IntermediateCalcs.IntermediateCalcContainer => {
  return {
    field: `placeholder${index}`,
    fieldType: 'optionalFloat',
    config: {
      type: 'formula',
      engineVersion: FormulaEngine.SupportedFormulaEngines.HyperFormula1,
      formula: '"TODO"',
    },
  };
};

type LoanProductIntermediateCalcProps = {
  baseFields: CreditAgreement.DataTapeField.BaseConfig[];
  currentErrors: any[];
  id: string;
  index: number;
  intermediateCalc: CreditAgreement.Config.LoanTapeField.IntermediateCalcs.IntermediateCalcContainer;
  intermediateCalcs: CreditAgreement.Config.LoanTapeField.IntermediateCalcs.IntermediateCalcContainer[];
  intermediateCalcsPath: string;
  isOrderLocked: boolean;
  moveRow: any;
  onDeleteField: Function;
  wipConfig?: CreditAgreementFullArtifactWipConfigType;
};
const LoanProductIntermediateCalc = ({
  baseFields,
  currentErrors,
  id,
  index,
  intermediateCalc,
  intermediateCalcs,
  intermediateCalcsPath,
  isOrderLocked,
  moveRow,
  onDeleteField,
  wipConfig,
}: LoanProductIntermediateCalcProps) => {
  const flags = useFeatureFlags();
  const [isIntermediateCalcEditOn, setIsIntermediateCalcEditOn] = useState(
    false,
  );

  const onSaveField = useCallback(
    (
      newIntermediateCalc: CreditAgreement.Config.LoanTapeField.IntermediateCalcs.IntermediateCalcContainer,
      setValidationErrors: React.Dispatch<React.SetStateAction<string[]>>,
    ) => {
      const newIntermediateCalcs = [...intermediateCalcs];
      newIntermediateCalcs[index] = newIntermediateCalc;

      const checkErrors = checkIntermediateCalcErrors({
        intermediateCalcs: newIntermediateCalcs,
        baseFields: baseFields.map(({ field }) => field),
      });
      const currentFieldErrors = checkErrors.filter(
        ({ field }) => field === newIntermediateCalc.field,
      );

      if (currentFieldErrors.length === 0) {
        if (wipConfig) {
          const basePropertyKey = `${intermediateCalcsPath}[${index}]`;
          const prettyPrintedNewIntermediateCalc = prettyPrintAllFormulas(
            newIntermediateCalc,
          );
          wipConfig.formProps.setFieldValue(
            basePropertyKey,
            prettyPrintedNewIntermediateCalc,
          );

          const updatedWipCreditAgreement = setIn(
            wipConfig.formProps.values,
            basePropertyKey,
            prettyPrintedNewIntermediateCalc,
          );
          wipConfig.saveCreditAgreementFullArtifactWip({
            variables: {
              creditAgreementFullArtifactWip: {
                id: updatedWipCreditAgreement.id,
                creditFacilityId: updatedWipCreditAgreement.creditFacilityId,
                creditAgreement: JSON.stringify(updatedWipCreditAgreement),
              },
            },
          });
        }

        setIsIntermediateCalcEditOn(false);
      } else {
        setValidationErrors(currentFieldErrors[0].errorMessages);
      }
    },
    [baseFields, index, intermediateCalcs, intermediateCalcsPath, wipConfig],
  );
  // return an EditIntermediateCalcRow if isIntermediateCalcModalOpen (rename this state too)
  if (!isIntermediateCalcEditOn || !isOrderLocked) {
    return (
      <TrDraggableHandle
        acceptedType="intermediateCalc"
        id={id}
        index={index}
        moveRow={moveRow}
        isDragDisabled={isOrderLocked}
      >
        <Td>{intermediateCalc.field}</Td>
        <Td>{intermediateCalc.fieldType || '-'}</Td>
        <Td>
          {getFormulaIcon(
            _.chain(currentErrors)
              .filter((err) => err.field === intermediateCalc.field)
              .first()
              .value() || null,
          )}
        </Td>
        <Td whiteSpace="pre-wrap">
          <FormulaContainer>
            {String(intermediateCalc.config.formula)}
          </FormulaContainer>
        </Td>
        {wipConfig?.isMutateAllowed(`${intermediateCalcsPath}[${index}]`) &&
          isOrderLocked && (
            <Td>
              <EditIconButton
                ariaLabel="view intermediate calc"
                label="View Intermediate Calc"
                isDisabled={
                  flags.isValleyBankLenderView &&
                  isRequiredIntermediateFieldName(intermediateCalc.field)
                }
                onClick={() => {
                  setIsIntermediateCalcEditOn(true);
                }}
              />
              <DeleteIconButton
                ariaLabel="delete intermediate calc"
                label="Delete Intermediate Calc"
                onClick={() => {
                  onDeleteField(index);
                }}
                isDisabled={isRequiredIntermediateFieldName(
                  intermediateCalc.field,
                )}
              />
            </Td>
          )}
      </TrDraggableHandle>
    );
  }

  return (
    <EditIntermediateCalcRow
      onCancel={() => {
        setIsIntermediateCalcEditOn(false);
      }}
      onSave={onSaveField}
      intermediateCalc={intermediateCalc}
      allIntermediateCalcNames={intermediateCalcs.map((ic) => ic.field)}
      id={id}
      index={index}
      moveRow={moveRow}
      isOrderLocked={isOrderLocked}
      operationMode={wipConfig?.operationMode}
    />
  );
};

type LoanProductIntermediateCalcsProps = {
  loanProductKey: string;
  config: CreditAgreement.Config.LoanProductMechanics.Config;
  wipConfig?: CreditAgreementFullArtifactWipConfigType;
  fields: CreditAgreement.DataTapeField.BaseConfig[];
};
export const LoanProductIntermediateCalcs = ({
  loanProductKey,
  config,
  wipConfig,
  fields,
}: LoanProductIntermediateCalcsProps) => {
  const toast = useToast();
  const {
    useRowState,
    useOrderLockedState,
    moveRow,
    ui: { LockOrderButton, RestoreOrderButton, SaveOrderCallout },
  } = useDraggableRow<CreditAgreement.Config.LoanTapeField.IntermediateCalcs.IntermediateCalcContainer>(
    { comparisonKey: 'field' },
  );
  const [intermediateCalcs, setIntermediateCalcs] = useRowState(
    config.intermediateCalcConfigs,
  );
  const [isOrderLocked] = useOrderLockedState();

  const [currentErrors, setCurrentErrors] = useState<IntermediateCalcError[]>(
    [],
  );

  const intermediateCalcsPath = `${loanProductKey}._config_.intermediateCalcConfigs`;

  const checkForCalcErrors = useCallback(
    (intermediateCalcsToCheck: any) => {
      const checkErrors: IntermediateCalcError[] = checkIntermediateCalcErrors({
        intermediateCalcs: intermediateCalcsToCheck,
        baseFields: fields.map(({ field }) => field),
      });

      if (checkErrors.length && !toast.isActive(errorToastId)) {
        const description = checkErrors.map((err) => (
          <Text key={err.field}>
            {err.field} errors: [{err.errorMessages.join(', ')}]
          </Text>
        ));
        errorToastAdminConfig.title = `There Are Errors In The Intermediate Calcs`;
        toast({ ...errorToastAdminConfig, description });
      }

      setCurrentErrors(checkErrors);
    },
    [fields, toast, setCurrentErrors],
  );

  useEffect(() => {
    if (!_.isEmpty(intermediateCalcs)) {
      checkForCalcErrors(intermediateCalcs);
    }
  }, [fields, checkForCalcErrors, intermediateCalcs]);

  const onDeleteField = (index: number) => {
    const newIntermediateCalcs = [...intermediateCalcs];
    newIntermediateCalcs.splice(index, 1);
    setIntermediateCalcs(newIntermediateCalcs);
    wipConfig?.formProps.setFieldValue(
      intermediateCalcsPath,
      newIntermediateCalcs,
    );

    checkForCalcErrors(newIntermediateCalcs);
  };

  const onSaveOrder = (
    newIntermediateCalcs: CreditAgreement.Config.LoanTapeField.IntermediateCalcs.IntermediateCalcContainer[],
  ) => {
    wipConfig?.formProps.setFieldValue(
      intermediateCalcsPath,
      newIntermediateCalcs,
    );
  };

  return (
    <>
      <Box mb="36px" width="100%">
        <Flex alignItems="center">
          {wipConfig?.isMutateAllowed(intermediateCalcsPath) && (
            <LockOrderButton
              isDisabled={!wipConfig}
              onSaveOrder={onSaveOrder}
            />
          )}
          <Text fontWeight="bold" fontSize="18px">
            Intermediate Calcs
          </Text>
          {wipConfig?.isMutateAllowed(intermediateCalcsPath) && (
            <RestoreOrderButton
              isDisabled={!wipConfig.isMutateAllowed(intermediateCalcsPath)}
            />
          )}
        </Flex>
        <Flex
          gap="1.5rem"
          flexWrap={['wrap', 'wrap', 'nowrap']}
          marginBottom="1.5rem"
        >
          <Text color={colors.black[70]}>
            {`The Required Intermediate Calcs are - ${RequiredIntermediateFieldNames.join(
              ', ',
            )} - these must be configured.`}
          </Text>
          {wipConfig?.isMutateAllowed(intermediateCalcsPath) && (
            <Button
              aria-label="Add Intermediate Calc"
              variant="secondary"
              colorScheme="v2-aurora"
              onClick={() => {
                const newIntermediateCalc = createIntermediateCalcShell(
                  intermediateCalcs.length,
                );
                wipConfig.formProps.setFieldValue(intermediateCalcsPath, [
                  ...intermediateCalcs,
                  newIntermediateCalc,
                ]);
              }}
              isDisabled={!isOrderLocked}
              gap="0.5rem"
              flexShrink={0}
            >
              <FinleyIcon icon="plus" />
              Add Intermediate Field
            </Button>
          )}
        </Flex>
        {wipConfig?.isMutateAllowed(intermediateCalcsPath) && (
          <SaveOrderCallout />
        )}
        {intermediateCalcs.length === 0 && (
          <Text>
            There are currently no intermediate calcs for this loan product.
          </Text>
        )}
        {intermediateCalcs.length > 0 && (
          <Flex width="100%" overflowY="auto">
            <Table borderWidth="1px">
              <Thead>
                <Tr>
                  <Th display={!isOrderLocked ? undefined : 'none'} />
                  <Th minWidth="10rem">Field Name</Th>
                  <Th minWidth="7rem">Type</Th>
                  <Th width="6rem" minWidth="6rem">
                    Valid
                  </Th>
                  <Th width="25rem" minWidth="25rem">
                    Formula / Method
                  </Th>
                  {wipConfig && isOrderLocked && (
                    <Th width="8.5rem" minWidth="8.5rem" />
                  )}
                </Tr>
              </Thead>
              <Tbody>
                {intermediateCalcs.map(
                  (intermediateCalc: any, index: number) => {
                    const id = getIterationKey(intermediateCalc);
                    return (
                      <LoanProductIntermediateCalc
                        baseFields={fields}
                        currentErrors={currentErrors}
                        index={index}
                        intermediateCalc={intermediateCalc}
                        intermediateCalcs={intermediateCalcs}
                        intermediateCalcsPath={intermediateCalcsPath}
                        isOrderLocked={isOrderLocked}
                        key={id}
                        id={id}
                        moveRow={moveRow}
                        onDeleteField={onDeleteField}
                        wipConfig={wipConfig}
                      />
                    );
                  },
                )}
              </Tbody>
            </Table>
          </Flex>
        )}
      </Box>
    </>
  );
};
